import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { profileApiService } from '../services/profileApi';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [companyData, setCompanyData] = useState({
    name: 'PoultryPro360',
    logo: null,
    loading: false,
    loaded: false,
  });

  // Empty effect - components will call loadCompanyData when ready
  useEffect(() => {
    // Cleanup blob URLs when component unmounts to prevent memory leaks
    return () => {
      if (companyData.logo && companyData.logo.startsWith('blob:')) {
        URL.revokeObjectURL(companyData.logo);
      }
    };
  }, [companyData.logo]);

  const loadCompanyData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    let shouldLoad = false;
    setCompanyData(prev => {
      if (prev.loading) {
        return prev; // Already loading, don't start another call
      }
      shouldLoad = true;
      return { ...prev, loading: true };
    });
    
    if (!shouldLoad) return;
    
    try {
      // Get user profile to check for company logo
      const response = await profileApiService.getCurrentUserProfile();
      
      if (response.responseData && response.responseData.companyLogo) {
        // Revoke old blob URL if it exists
        setCompanyData(prev => {
          if (prev.logo && prev.logo.startsWith('blob:')) {
            URL.revokeObjectURL(prev.logo);
          }
          return prev;
        });
        
        // Fetch logo as blob with authentication
        const logoBlobUrl = await profileApiService.getCompanyLogoImageBlob();
        
        setCompanyData(prev => ({
          ...prev,
          logo: logoBlobUrl,
          name: prev.name,
          loading: false,
          loaded: true,
        }));
      } else {
        setCompanyData(prev => ({ 
          ...prev,
          logo: null,
          loading: false,
          loaded: true,
        }));
      }
    } catch (error) {
      console.error('CompanyContext: Error loading company data:', error);
      setCompanyData(prev => ({ 
        ...prev, 
        loading: false,
        loaded: true,
      }));
    }
  }, []);

  const updateCompanyLogo = async (logoFile) => {
    try {
      const response = await profileApiService.uploadCompanyLogo(logoFile);
      // Fetch the uploaded logo as blob with authentication
      const logoBlobUrl = await profileApiService.getCompanyLogoImageBlob();
      setCompanyData(prev => ({
        ...prev,
        logo: logoBlobUrl,
      }));
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateCompanyLogoUrl = useCallback((logoUrl) => {
    setCompanyData(prev => {
      // Revoke old blob URL if it exists
      if (prev.logo && prev.logo.startsWith('blob:')) {
        URL.revokeObjectURL(prev.logo);
      }
      
      return {
        ...prev,
        logo: logoUrl,
      };
    });
  }, []);

  const updateCompanyName = useCallback((name) => {
    setCompanyData(prev => ({
      ...prev,
      name: name || 'PoultryPro360',
    }));
  }, []);

  const forceRefreshLogo = useCallback(async () => {
    try {
      // Revoke old blob URL if it exists
      setCompanyData(prev => {
        if (prev.logo && prev.logo.startsWith('blob:')) {
          URL.revokeObjectURL(prev.logo);
        }
        return prev;
      });
      
      // Fetch a fresh blob URL with authentication
      const logoBlobUrl = await profileApiService.getCompanyLogoImageBlob();
      if (logoBlobUrl) {
        setCompanyData(prev => ({
          ...prev,
          logo: logoBlobUrl,
        }));
      }
    } catch (error) {
      console.error('Failed to refresh logo:', error);
    }
  }, []);

  const value = {
    companyData,
    updateCompanyLogo,
    updateCompanyLogoUrl,
    updateCompanyName,
    loadCompanyData,
    refreshCompanyData: loadCompanyData,
    forceRefreshLogo,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;